import axios from "axios";

const BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const API_BASE_URL = `${BASE}/api/public/livestock`;

const createApiClient = () => {
  const token = localStorage.getItem('authToken');

  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
};

const livestockService = {
  // Livestock CRUD
  getByBatchId: async (batchId) => {
    const client = createApiClient();
    const { data } = await client.get(`/batch/${batchId}`);
    return data;
  },

  getById: async (id) => {
    const client = createApiClient();
    const { data } = await client.get(`/${id}`);
    return data;
  },

  create: async (payload) => {
    const client = createApiClient();
    const { data } = await client.post('', payload);
    return data;
  },

  getNextAnimalId: async (type = 'GOAT') => {
    const client = createApiClient();
    const { data } = await client.get('/next-animal-id', { params: { type } });
    return data?.suggestedAnimalId || '';
  },

  update: async (id, payload) => {
    const client = createApiClient();
    const { data } = await client.put(`/${id}`, payload);
    return data;
  },

  delete: async (id) => {
    const client = createApiClient();
    await client.delete(`/${id}`);
  },


  deleteMany: async (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    const client = createApiClient();
    try {
      await client.post('/bulk-delete', { ids });
    } catch (err) {
      // Backward-compatible fallback for older backend builds.
      await Promise.all(ids.map((id) => livestockService.delete(id)));
    }
  },

  recordBulkSales: async (items) => {
    const client = createApiClient();
    const { data } = await client.post('/bulk-sales', {items});
    return data;
  },

  markSoldMany: async (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    const client = createApiClient();
    try {
      const { data } = await client.post('/bulk-mark-sold', { ids });
      return data;
    } catch (err) {
      // Backward-compatible fallback for older backend builds.
      const updates = ids.map(async (id) => {
        const existing = await livestockService.getById(id);
        return livestockService.update(id, {
          ...existing,
          currentStatus: 'SOLD',
          sold: true,
          quantity: 1,
        });
      });
      return Promise.all(updates);
    }
  },

  // Weight APIs
  getWeights: async (livestockId) => {
    const client = createApiClient();
    const { data } = await client.get(`/${livestockId}/weights`);
    return data;
  },

  getWeightSummary: async (livestockId) => {
    const client = createApiClient();
    const { data } = await client.get(`/${livestockId}/weights/summary`);
    return data;
  },

  addWeight: async (livestockId, payload) => {
    const client = createApiClient();
    const { data } = await client.post(`/${livestockId}/weights`, {
      weightKg: payload.weightKg,
      measuredAt: payload.measuredAt,
      notes: payload.notes ?? null,
    });
    return data;
  },

  updateWeight: async (livestockId, weightRecordId, payload) => {
    const client = createApiClient();
    const { data } = await client.put(`/${livestockId}/weights/${weightRecordId}`, {
      weightKg: payload.weightKg,
      measuredAt: payload.measuredAt,
      notes: payload.notes ?? null,
    });
    return data;
  },

  deleteWeight: async (livestockId, weightRecordId) => {
    const client = createApiClient();
    await client.delete(`/${livestockId}/weights/${weightRecordId}`);
  },
};

export default livestockService;
